import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { trigger, transition, style, animate } from '@angular/animations';
import { LlmService } from 'app/services/llm.service';

declare var io: any;

interface ChatMessage {
  sender: 'user' | 'ai';
  text: string;
  timestamp: Date;
}

interface SpeechRecognition extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: Event) => void) | null;
  onend: (() => void) | null;
}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

@Component({
  selector: 'app-live-speak-assist',
  templateUrl: './live-speak-assist.component.html',
  styleUrls: ['./live-speak-assist.component.scss'],
  animations: [
    trigger('wordFadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class LiveSpeakAssistComponent implements OnInit, OnDestroy {

  messages: ChatMessage[] = [];

  isListening = false;
  isProcessing = false;
  isSpeaking = false;
  aiResponseText = '';

  recognition!: SpeechRecognition | null;
  speechSynthesis: SpeechSynthesis = window.speechSynthesis;

  mediaRecorder: MediaRecorder | null = null;
  audioStream: MediaStream | null = null;
  audioChunks: Blob[] = [];
  currentAudioBlob: Blob | null = null;

  words: string[] = [];
  visibleWords: string[] = [];
  currentWordIndex = 0;
  subtitleInterval: ReturnType<typeof setInterval> | null = null;
  displaySentences: string[] = [];
  allSentences: string[] = [];
  visibleSentences: string[] = [];
  currentSentenceIndex = 0;
  sentenceInterval: ReturnType<typeof setInterval> | null = null;
  sentenceTimeout: ReturnType<typeof setTimeout> | null = null;
  currentSentenceWords: string[] = [];
  visibleSentenceWords: string[] = [];
  currentSentenceWordIndex = 0;
  sentenceWordInterval: ReturnType<typeof setInterval> | null = null;

  private socket: any = null;
  private socketConnected: boolean = false;
  private pendingRequest: { text: string } | null = null;
  private audioContext: AudioContext | null = null;
  private audioQueue: ArrayBuffer[] = [];
  private isPlayingAudio = false;
  private wordTimestamps: Array<{ word: string, start: number, end: number, sentence_index: number }> = [];
  private audioStartTime: number = 0;
  private totalSentences: number = 0;
  private sampleRate: number = 16000;
  private completedSentences: number = 0;

  private pageUnloadHandler = () => this.handlePageUnload();
  private visibilityChangeHandler = () => this.handleVisibilityChange();

  constructor(
    private router: Router,
    private llmService: LlmService,
    private cdr: ChangeDetectorRef
  ) { }


  ngOnInit(): void {
    if (window.speechSynthesis) {
      try {
        window.speechSynthesis.cancel();
        setTimeout(() => {
          if (window.speechSynthesis) {
            window.speechSynthesis.cancel();
          }
        }, 100);
      } catch (e) {
      }
    }

    this.stopSpeaking();
    this.resetVoiceState();

    window.addEventListener('beforeunload', this.pageUnloadHandler);

    document.addEventListener('visibilitychange', this.visibilityChangeHandler);

    this.initializeSpeechRecognition();

    setTimeout(() => {
      this.initializeSocket();
    }, 100);
  }

  ngOnDestroy(): void {
    window.removeEventListener('beforeunload', this.pageUnloadHandler);
    document.removeEventListener('visibilitychange', this.visibilityChangeHandler);

    this.cleanupResources();
    this.disconnectSocket();
  }


  private initializeSpeechRecognition(): void {
    const SpeechRecognitionConstructor =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognitionConstructor) {
      console.warn('Speech recognition not supported');
      this.recognition = null;
      return;
    }

    this.recognition = new SpeechRecognitionConstructor() as SpeechRecognition;
    this.recognition.lang = 'en-US';
    this.recognition.continuous = false;
    this.recognition.interimResults = false;

    this.recognition.onresult = (event: SpeechRecognitionEvent) =>
      this.handleSpeechResult(event);

    this.recognition.onerror = () => (this.isListening = false);
    this.recognition.onend = () => (this.isListening = false);
  }

  toggleListening(): void {
    if (!this.recognition) {
      alert('Speech recognition not supported in this browser');
      return;
    }

    this.isListening ? this.stopListening() : this.startListening();
  }

  async startListening(): Promise<void> {
    if (this.isProcessing || this.isSpeaking) return;

    try {
      this.stopSpeaking();
      this.aiResponseText = '';
      this.words = [];
      this.visibleWords = [];
      this.currentWordIndex = 0;
      this.displaySentences = [];
      this.currentSentenceWords = [];
      this.visibleSentenceWords = [];
      this.currentSentenceWordIndex = 0;
      this.allSentences = [];
      this.visibleSentences = [];
      this.currentSentenceIndex = 0;

      this.isListening = true;
      await this.startAudioRecording();
      this.recognition.start();
    } catch (err) {
      this.isListening = false;
      this.stopAudioRecording();
    }
  }

  async stopListening(): Promise<void> {
    this.isListening = false;
    this.recognition?.stop();
    await this.stopAudioRecording();
  }

  private async handleSpeechResult(event: SpeechRecognitionEvent): Promise<void> {
    const transcript = event.results[0][0].transcript;
    this.addMessage('user', transcript);

    await this.stopAudioRecording();
    this.processUserInput(transcript);
  }


  private async startAudioRecording(): Promise<void> {
    this.audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });

    this.audioChunks = [];
    this.mediaRecorder = new MediaRecorder(this.audioStream, {
      mimeType: 'audio/webm'
    });

    this.mediaRecorder.ondataavailable = e => {
      if (e.data.size) this.audioChunks.push(e.data);
    };

    this.mediaRecorder.onstop = () => {
      if (this.audioChunks.length) {
        this.currentAudioBlob = new Blob(this.audioChunks, {
          type: 'audio/webm'
        });
      }
      this.audioStream?.getTracks().forEach(t => t.stop());
      this.audioStream = null;
    };

    this.mediaRecorder.start();
  }

  private async stopAudioRecording(): Promise<void> {
    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      this.mediaRecorder.stop();
    }
    this.audioStream?.getTracks().forEach(t => t.stop());
    this.audioStream = null;
  }


  async processUserInput(text: string): Promise<void> {
    if (!this.socket) {
      console.error('Socket not initialized');
      this.handleAIError({ message: 'Socket not initialized. Please refresh the page.' });
      return;
    }

    if (!this.socketConnected || !this.socket.connected) {
      console.log('Socket not connected yet, queuing request...');

      if (!this.pendingRequest) {
        this.pendingRequest = { text: text };
      }

      let attempts = 0;
      const maxAttempts = 250;

      while (!this.socketConnected && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;

        if (this.socket && this.socket.connected) {
          this.socketConnected = true;
          break;
        }
      }

      if (!this.socketConnected) {
        console.error('Socket connection timeout after', attempts * 100, 'ms');
        console.error('Socket state:', {
          socketExists: !!this.socket,
          connected: this.socket?.connected,
          disconnected: this.socket?.disconnected,
          id: this.socket?.id
        });
        this.pendingRequest = null;
        this.handleAIError({ message: 'Connection timeout. The TTS server may be unavailable. Please check your network connection and try again.' });
        return;
      }

      if (this.pendingRequest && this.pendingRequest.text === text) {
        return;
      }
    }

    this.isProcessing = true;

    try {
      this.audioQueue = [];
      this.wordTimestamps = [];
      this.visibleWords = [];
      this.currentWordIndex = 0;
      this.aiResponseText = '';
      this.isSpeaking = true;
      this.totalSentences = 0;
      this.completedSentences = 0;
      this.audioStartTime = 0;

      const payload = {
        text: text,
        voice: "af_bella",
        sample_rate: 16000
      };

      if (this.socket && this.socket.connected) {
        console.log('Emitting start_streaming with payload:', payload);
        this.socket.emit('start_streaming', payload);
      } else {
        console.error('Socket not connected after wait. Socket state:', {
          socketExists: !!this.socket,
          connected: this.socket?.connected,
          socketConnected: this.socketConnected
        });
        this.isProcessing = false;
        this.isSpeaking = false;
        this.handleAIError({ message: 'Socket connection lost. Please try again.' });
      }
    } catch (err) {
      console.error('Error processing user input:', err);
      this.isProcessing = false;
      this.isSpeaking = false;
      this.handleAIError(err);
    }
  }

  private async buildPayload(text: string): Promise<any> {
    const payload: any = {
      query: [text],
      bypass_cache: true,
      client_id: localStorage.getItem('company_id')
    };

    if (this.currentAudioBlob) {
      payload.voice_audio = await this.blobToBase64(this.currentAudioBlob);
      payload.voice_format = this.currentAudioBlob.type;
      this.currentAudioBlob = null;
    }

    return payload;
  }

  private handleAIResponse(response: any): void {
    this.isProcessing = false;
    const text = response?.[0]?.answer_plain ?? 'No response available';
    this.updateSentences(text);
    this.aiResponseText = text;
    this.respondWithSpeech(text);
  }

  private handleAIError(error: HttpErrorResponse | any): void {
    this.isProcessing = false;
    this.isSpeaking = false;

    let msg = 'Unknown error occurred';
    if (error?.error?.message) {
      msg = error.error.message;
    } else if (error?.message) {
      msg = error.message;
    } else if (typeof error === 'string') {
      msg = error;
    }

    const errorText = `Error: ${msg}`;
    this.updateSentences(errorText);
    this.aiResponseText = errorText;
    this.addMessage('ai', errorText);

    this.audioQueue = [];
    this.isPlayingAudio = false;
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    this.cdr.detectChanges();
  }

  private respondWithSpeech(text: string): void {
    this.addMessage('ai', text);
    this.speakText(text);
  }


  speakText(text: string): void {
    if (!this.speechSynthesis) return;

    this.stopSpeaking();

    this.splitIntoSentences(text);
    this.currentSentenceIndex = 0;
    this.visibleSentences = [];
    this.displaySentences = [];

    this.words = text.split(/(\s+)/).filter(word => word.trim().length > 0);
    this.currentWordIndex = 0;
    this.visibleWords = [];

    if (this.allSentences.length === 0) {
      return;
    }

    this.speakSentencesSequentially();
  }

  private speakSentencesSequentially(): void {
    if (this.allSentences.length === 0) return;

    this.isSpeaking = true;
    let currentIndex = 0;

    const speakNextSentence = () => {
      if (currentIndex >= this.allSentences.length) {
        this.isSpeaking = false;
        this.cleanupSentenceDisplay('');
        return;
      }

      const sentence = this.allSentences[currentIndex];
      const utterance = new SpeechSynthesisUtterance(sentence);
      utterance.lang = 'en-US';

      utterance.onstart = () => {
        this.visibleSentences = [sentence];
        this.displaySentences = [sentence];
        this.currentSentenceIndex = currentIndex;

        this.startSentenceWordAnimation(sentence);

        this.cdr.detectChanges();
      };

      utterance.onend = () => {
        this.cleanupSentenceWordAnimation();
        currentIndex++;
        speakNextSentence();
      };

      utterance.onerror = () => {
        this.cleanupSentenceWordAnimation();
        currentIndex++;
        speakNextSentence();
      };

      this.speechSynthesis.speak(utterance);
    };

    speakNextSentence();
  }

  private startSentenceWordAnimation(sentence: string): void {
    this.cleanupSentenceWordAnimation();

    this.currentSentenceWords = sentence.split(/(\s+)/).filter(word => word.trim().length > 0);
    this.currentSentenceWordIndex = 0;
    this.visibleSentenceWords = [];

    if (this.currentSentenceWords.length === 0) {
      return;
    }

    const wordsPerSecond = 2.5;
    const delayPerWord = 1000 / wordsPerSecond;

    this.visibleSentenceWords = [this.currentSentenceWords[0]];
    this.currentSentenceWordIndex = 1;
    this.cdr.detectChanges();

    this.sentenceWordInterval = setInterval(() => {
      if (this.currentSentenceWordIndex < this.currentSentenceWords.length) {
        const word = this.currentSentenceWords[this.currentSentenceWordIndex];
        this.visibleSentenceWords.push(word);
        this.currentSentenceWordIndex++;
        this.cdr.detectChanges();
      } else {
        if (this.sentenceWordInterval) {
          clearInterval(this.sentenceWordInterval);
          this.sentenceWordInterval = null;
        }
        this.cdr.detectChanges();
      }
    }, delayPerWord);
  }

  private cleanupSentenceWordAnimation(): void {
    if (this.sentenceWordInterval) {
      clearInterval(this.sentenceWordInterval);
      this.sentenceWordInterval = null;
    }

    if (this.currentSentenceWords.length > 0) {
      this.visibleSentenceWords = [...this.currentSentenceWords];
    }

    this.currentSentenceWords = [];
    this.currentSentenceWordIndex = 0;
  }

  private splitIntoSentences(text: string): void {
    const sentenceEndings = /[.!?]+/g;
    const sentences: string[] = [];
    let lastIndex = 0;
    let match;

    while ((match = sentenceEndings.exec(text)) !== null) {
      const sentenceEnd = match.index + match[0].length;
      const sentence = text.substring(lastIndex, sentenceEnd).trim();
      if (sentence.length > 0) {
        sentences.push(sentence);
      }
      lastIndex = sentenceEnd;

      while (lastIndex < text.length && /\s/.test(text[lastIndex])) {
        lastIndex++;
      }
    }

    const remaining = text.substring(lastIndex).trim();
    if (remaining.length > 0) {
      sentences.push(remaining);
    }

    this.allSentences = sentences;
  }


  private startSynchronizedSubtitle(text: string): void {
    const wordsPerSecond = 2.5;
    const delayPerWord = 1000 / wordsPerSecond;

    this.currentWordIndex = 0;
    this.visibleWords = [];

    if (this.words.length > 0) {
      this.visibleWords = [this.words[0]];
      this.currentWordIndex = 1;
      this.cdr.detectChanges();
    }

    this.subtitleInterval = setInterval(() => {
      if (this.currentWordIndex < this.words.length) {
        const word = this.words[this.currentWordIndex];
        this.visibleWords.push(word);
        this.currentWordIndex++;
        this.cdr.detectChanges();
      } else {
        if (this.subtitleInterval) {
          clearInterval(this.subtitleInterval);
          this.subtitleInterval = null;
        }
        this.cdr.detectChanges();
      }
    }, delayPerWord);
  }

  private cleanupSentenceDisplay(text: string = ''): void {
    this.isSpeaking = false;

    this.cleanupSentenceWordAnimation();

    if (this.sentenceTimeout) {
      clearTimeout(this.sentenceTimeout);
      this.sentenceTimeout = null;
    }

    if (this.sentenceInterval) {
      clearInterval(this.sentenceInterval);
      this.sentenceInterval = null;
    }

    if (this.subtitleInterval) {
      clearInterval(this.subtitleInterval);
      this.subtitleInterval = null;
    }

    if (text && this.allSentences.length > 0 && this.visibleSentences.length > 0) {
      this.displaySentences = [...this.visibleSentences];
    } else if (text && this.allSentences.length > 0) {
      this.displaySentences = [this.allSentences[this.allSentences.length - 1]];
    }

    if (text && this.words.length > 0) {
      this.visibleWords = [...this.words];
    }

    this.cdr.detectChanges();
  }

  private cleanupSubtitle(text: string = ''): void {
    this.isSpeaking = false;

    if (this.subtitleInterval) {
      clearInterval(this.subtitleInterval);
      this.subtitleInterval = null;
    }

    if (text && this.words.length > 0) {
      this.visibleWords = [...this.words];
    }

    this.cdr.detectChanges();
  }

  stopSpeaking(): void {
    if (this.speechSynthesis) {
      if (this.speechSynthesis.speaking || this.speechSynthesis.pending) {
        this.speechSynthesis.cancel();
      }
      try {
        this.speechSynthesis.cancel();
      } catch (e) {
      }
    }

    this.audioQueue = [];
    this.isPlayingAudio = false;
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.audioStartTime = 0;

    this.cleanupSentenceDisplay();
  }

  addMessage(sender: 'user' | 'ai', text: string): void {
    this.messages.push({ sender, text, timestamp: new Date() });
  }

  clearMessages(): void {
    this.stopSpeaking();

    if (this.isListening) {
      this.stopListening();
    }

    this.messages = [];
    this.aiResponseText = '';
    this.words = [];
    this.visibleWords = [];
    this.currentWordIndex = 0;
    this.displaySentences = [];
    this.allSentences = [];
    this.visibleSentences = [];
    this.currentSentenceIndex = 0;
    this.currentSentenceWords = [];
    this.visibleSentenceWords = [];
    this.currentSentenceWordIndex = 0;
    this.isProcessing = false;
  }

  navigateBack(): void {
    this.router.navigate(['/aiassist']);
  }

  private handlePageUnload(): void {
    this.stopSpeaking();
    this.stopListening();
    this.resetVoiceState();
  }

  private handleVisibilityChange(): void {
    if (document.hidden) {
      this.stopSpeaking();
      this.resetVoiceState();
    }
  }

  private resetVoiceState(): void {
    this.isSpeaking = false;
    this.isListening = false;
    this.aiResponseText = '';
    this.words = [];
    this.visibleWords = [];
    this.currentWordIndex = 0;
    this.displaySentences = [];
    this.allSentences = [];
    this.visibleSentences = [];
    this.currentSentenceIndex = 0;
    this.currentSentenceWords = [];
    this.visibleSentenceWords = [];
    this.currentSentenceWordIndex = 0;

    if (this.subtitleInterval) {
      clearInterval(this.subtitleInterval);
      this.subtitleInterval = null;
    }

    if (this.sentenceTimeout) {
      clearTimeout(this.sentenceTimeout);
      this.sentenceTimeout = null;
    }

    if (this.sentenceInterval) {
      clearInterval(this.sentenceInterval);
      this.sentenceInterval = null;
    }

    if (this.sentenceWordInterval) {
      clearInterval(this.sentenceWordInterval);
      this.sentenceWordInterval = null;
    }

    if (this.speechSynthesis) {
      try {
        if (this.speechSynthesis.speaking || this.speechSynthesis.pending) {
          this.speechSynthesis.cancel();
        }
        this.speechSynthesis.cancel();
      } catch (e) {
        console.warn('Error canceling speech synthesis:', e);
      }
    }
  }

  get displayWords(): string[] {
    if (this.visibleWords.length > 0) {
      return this.visibleWords;
    }
    if (this.aiResponseText && !this.isSpeaking) {
      return this.aiResponseText.split(/\s+/).filter(word => word.trim().length > 0);
    }
    return [];
  }

  private updateSentences(newText: string): void {
    if (this.isSpeaking) {
      return;
    }

    if (!newText) {
      this.displaySentences = [];
      return;
    }

    const sentenceEndings = /[.!?]+/g;
    const sentences: string[] = [];
    let lastIndex = 0;
    let match;

    while ((match = sentenceEndings.exec(newText)) !== null) {
      const sentenceEnd = match.index + match[0].length;
      const sentence = newText.substring(lastIndex, sentenceEnd).trim();
      if (sentence.length > 0) {
        sentences.push(sentence);
      }
      lastIndex = sentenceEnd;

      while (lastIndex < newText.length && /\s/.test(newText[lastIndex])) {
        lastIndex++;
      }
    }

    const remaining = newText.substring(lastIndex).trim();
    if (remaining.length > 0) {
      sentences.push(remaining);
    }

    if (this.displaySentences.length > 0 && this.aiResponseText) {
      if (newText.startsWith(this.aiResponseText)) {
        const keptSentences: string[] = [];
        let searchIndex = 0;

        for (const oldSentence of this.displaySentences) {
          const trimmedOld = oldSentence.trim();
          const index = newText.indexOf(trimmedOld, searchIndex);
          if (index !== -1) {
            keptSentences.push(oldSentence);
            searchIndex = index + trimmedOld.length;
          } else {
            break;
          }
        }

        const remainingText = newText.substring(searchIndex).trim();
        if (remainingText) {
          const newSentences: string[] = [];
          let remLastIndex = 0;
          let remMatch;
          const remSentenceEndings = /[.!?]+/g;

          while ((remMatch = remSentenceEndings.exec(remainingText)) !== null) {
            const remSentenceEnd = remMatch.index + remMatch[0].length;
            const remSentence = remainingText.substring(remLastIndex, remSentenceEnd).trim();
            if (remSentence.length > 0) {
              newSentences.push(remSentence);
            }
            remLastIndex = remSentenceEnd;
            while (remLastIndex < remainingText.length && /\s/.test(remainingText[remLastIndex])) {
              remLastIndex++;
            }
          }

          const remRemaining = remainingText.substring(remLastIndex).trim();
          if (remRemaining.length > 0) {
            newSentences.push(remRemaining);
          }

          keptSentences.push(...newSentences);
        }

        this.displaySentences = keptSentences;
      } else {
        this.displaySentences = sentences;
      }
    } else {
      this.displaySentences = sentences;
    }

    this.cdr.detectChanges();
  }

  private cleanupResources(): void {
    this.stopSpeaking();
    this.stopAudioRecording();
    this.resetVoiceState();

    this.audioQueue = [];
    this.isPlayingAudio = false;
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.audioStartTime = 0;
    this.wordTimestamps = [];
    this.totalSentences = 0;
    this.completedSentences = 0;
    this.sampleRate = 16000;
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () =>
        resolve((reader.result as string).split(',')[1]);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  private initializeSocket(): void {
    try {
      const socketIO = (typeof io !== 'undefined') ? io : (window as any).io;

      if (!socketIO) {
        console.error('Socket.io client not found. Make sure socket.io-client is loaded.');
        this.handleAIError({ message: 'Socket.io client not available. Please refresh the page.' });
        return;
      }

      console.log('Initializing socket connection to wss://memailllm.canstringai.com');

      this.socket = socketIO('wss://memailllm.canstringai.com', {
        transports: ['polling', 'websocket'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: Infinity,
        timeout: 20000,
        forceNew: false,
        upgrade: true,
        rememberUpgrade: true
      });

      console.log('Socket instance created:', this.socket);

      this.socket.on('connect', () => {
        console.log('✓ Connected to TTS server, socket ID:', this.socket.id);
        this.socketConnected = true;
        this.cdr.detectChanges();

        if (this.pendingRequest) {
          const request = this.pendingRequest;
          this.pendingRequest = null;
          setTimeout(() => {
            this.processUserInput(request.text);
          }, 100);
        }
      });

      this.socket.on('disconnect', (reason: string) => {
        console.log('✗ Disconnected:', reason);
        this.socketConnected = false;
        this.isProcessing = false;
        this.isSpeaking = false;
        this.cdr.detectChanges();
      });

      this.socket.on('connect_error', (error: any) => {
        console.error('Socket connection error:', error);
        console.error('Error details:', {
          message: error.message,
          description: error.description,
          type: error.type,
          context: error.context,
          transport: error.transport
        });
        this.socketConnected = false;

        if (!this.isProcessing) {
          this.cdr.detectChanges();
        }
      });

      this.socket.on('reconnect', (attemptNumber: number) => {
        console.log('✓ Reconnected after', attemptNumber, 'attempts');
        this.socketConnected = true;
        this.cdr.detectChanges();
      });

      this.socket.on('reconnect_error', (error: any) => {
        console.error('Reconnection error:', error);
      });

      this.socket.on('reconnect_failed', () => {
        console.error('✗ Reconnection failed');
        this.socketConnected = false;
        if (this.pendingRequest) {
          this.handleAIError({ message: 'Failed to connect to TTS server. Please check if the server is running and try again.' });
        }
        this.cdr.detectChanges();
      });

      setTimeout(() => {
        if (!this.socketConnected && this.socket && !this.socket.connected) {
          console.warn('Socket connection taking longer than expected...');
        }
      }, 5000);

      this.socket.on('stream_started', (data: any) => {
        console.log('Stream started');
        console.log('Total sentences:', data.total_sentences);
        console.log('Sample rate:', data.sample_rate);
        this.totalSentences = data.total_sentences || 0;
        this.sampleRate = data.sample_rate || 16000;

        if (!this.audioContext) {
          this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
            sampleRate: this.sampleRate
          });
        }

        if (this.audioStartTime === 0) {
          this.audioStartTime = Date.now();
        }
      });

      this.socket.on('word_timestamp', (data: any) => {
        this.handleWordTimestamp(data);
      });

      this.socket.on('audio_chunk', (data: any) => {
        this.handleAudioChunk(data);
      });

      this.socket.on('sentence_complete', (data: any) => {
        console.log(`Sentence ${data.sentence_index} complete: "${data.sentence}"`);
        this.completedSentences = (data.sentence_index || 0) + 1;
        this.cdr.detectChanges();
      });

      this.socket.on('stream_complete', (data: any) => {
        console.log('All sentences complete!');
        console.log('Total sentences:', data?.total_sentences || this.totalSentences);
        this.handleStreamComplete();
      });

      this.socket.on('error', (data: any) => {
        console.error('Server error:', data?.error || data);
        this.handleAIError(data);
      });

    } catch (err) {
      console.error('Error initializing socket:', err);
    }
  }

  private disconnectSocket(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
  }

  private handleAudioChunk(data: any): void {
    try {
      console.log(`Received audio chunk ${data.chunk_index}`);

      if (!data.chunk) {
        console.warn('Audio chunk missing chunk data:', data);
        return;
      }

      const audioData = this.base64ToArrayBuffer(data.chunk);

      this.audioQueue.push(audioData);

      if (!this.isPlayingAudio) {
        this.processAudioQueue();
      }
    } catch (err) {
      console.error('Error handling audio chunk:', err);
    }
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  private async processAudioQueue(): Promise<void> {
    if (this.isPlayingAudio || this.audioQueue.length === 0) {
      return;
    }

    this.isPlayingAudio = true;

    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: this.sampleRate || 16000
      });
    }

    while (this.audioQueue.length > 0) {
      const chunk = this.audioQueue.shift();
      if (chunk) {
        try {
          const pcmData = new Int16Array(chunk);
          const audioBuffer = this.audioContext.createBuffer(1, pcmData.length, this.sampleRate);
          const channelData = audioBuffer.getChannelData(0);

          for (let i = 0; i < pcmData.length; i++) {
            channelData[i] = pcmData[i] / 32768.0;
          }

          const source = this.audioContext.createBufferSource();
          source.buffer = audioBuffer;
          source.connect(this.audioContext.destination);

          await new Promise<void>((resolve) => {
            source.onended = () => resolve();
            source.start(0);
          });
        } catch (err) {
          console.error('Error playing audio chunk:', err);
        }
      }
    }

    this.isPlayingAudio = false;
  }

  private handleWordTimestamp(data: any): void {
    try {
      console.log(`Word: "${data.word}" from ${data.start}s to ${data.end}s`);

      if (!data.word || typeof data.start === 'undefined') {
        console.warn('Invalid word timestamp data:', data);
        return;
      }

      this.wordTimestamps.push({
        word: data.word,
        start: data.start,
        end: data.end,
        sentence_index: data.sentence_index || 0
      });

      const fullText = this.wordTimestamps.map(t => t.word).join(' ');
      this.aiResponseText = fullText;
      this.words = this.wordTimestamps.map(t => t.word);

      this.updateSentences(fullText);

      this.displaySubtitle(data.word);

      if (!this.subtitleInterval) {
        this.startSynchronizedWordDisplay();
      }

      this.cdr.detectChanges();
    } catch (err) {
      console.error('Error handling word timestamp:', err);
    }
  }

  private displaySubtitle(word: string): void {
  }

  private startSynchronizedWordDisplay(): void {
    if (this.subtitleInterval) {
      clearInterval(this.subtitleInterval);
      this.subtitleInterval = null;
    }

    if (this.wordTimestamps.length === 0) {
      return;
    }

    let currentTimestampIndex = 0;

    const updateWords = () => {
      if (currentTimestampIndex >= this.wordTimestamps.length) {
        if (this.subtitleInterval) {
          clearInterval(this.subtitleInterval);
          this.subtitleInterval = null;
        }
        return;
      }

      const elapsed = (Date.now() - this.audioStartTime) / 1000;
      const currentTimestamp = this.wordTimestamps[currentTimestampIndex];

      if (elapsed >= currentTimestamp.start) {
        if (this.visibleWords.length <= currentTimestampIndex) {
          this.visibleWords.push(currentTimestamp.word);
          this.currentWordIndex++;
        }
        currentTimestampIndex++;
        this.cdr.detectChanges();
      }
    };

    this.subtitleInterval = setInterval(updateWords, 50);
  }

  private handleStreamComplete(): void {
    this.isProcessing = false;
    this.isSpeaking = false;

    if (this.wordTimestamps.length > 0) {
      const allWords = this.wordTimestamps.map(t => t.word);
      this.visibleWords = [...allWords];
      this.words = [...allWords];
      this.cdr.detectChanges();
    }

    if (this.subtitleInterval) {
      clearInterval(this.subtitleInterval);
      this.subtitleInterval = null;
    }

    this.audioStartTime = 0;
    this.completedSentences = 0;
    this.totalSentences = 0;

    if (this.aiResponseText && !this.messages.some(m => m.text === this.aiResponseText && m.sender === 'ai')) {
      this.addMessage('ai', this.aiResponseText);
    }

    this.cdr.detectChanges();
  }
}
